import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, FileDown, ChevronLeft, ChevronRight, Eye, Check, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PAGE, SAMPLE_ROW, STOCK_DESIGNS, newId, seedDesigns, stockDesignFor, categoryLabel } from "@/lib/studio/templates";
import { buildPdf } from "@/lib/studio/render";
import { StudioCanvas } from "@/components/studio/StudioCanvas";
import { ElementInspector } from "@/components/studio/ElementInspector";
import { DesignsPanel, InsertToolbar, SignatureLibrary } from "@/components/studio/DesignsPanel";
import { DataPanel } from "@/components/studio/DataPanel";
import { UnlockGate } from "@/components/studio/UnlockGate";

// The gate is in-memory only: leaving the Studio (or logging out) re-locks it.
export default function GraphicStudio() {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <UnlockGate onUnlocked={() => setUnlocked(true)} />;
  return <Studio />;
}

const LAST_KEY = "ccs_studio_last_design";
const AUTOSAVE_MS = 900;

function Studio() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [design, setDesign] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | error
  const [selectedId, setSelectedId] = useState(null);
  const [saved, setSaved] = useState([]);
  const [category, setCategory] = useState("top_scorers");
  const [source, setSource] = useState("top_scorers");
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(params.get("season_id") || "");
  const [club, setClub] = useState({});
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [previewIdx, setPreviewIdx] = useState(0);
  const [layout, setLayout] = useState("a4");
  const [nameOpen, setNameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(2.2);
  const [height, setHeight] = useState(700);
  const wrapRef = useRef(null);
  const rootRef = useRef(null);
  const designRef = useRef(null);
  designRef.current = design;

  const clubName = club?.name || user?.club_name || "Camera Club";

  useEffect(() => {
    Promise.all([
      api.get("/seasons"),
      api.get("/clubs/me").catch(() => ({ data: {} })),
      api.post("/graphic-studio/designs/seed", { designs: seedDesigns() }),
    ]).then(([s, c, d]) => {
      setSeasons(s.data);
      setClub(c.data || {});
      setSaved(d.data);
      if (!seasonId) setSeasonId(s.data.find((x) => x.is_active)?.id || s.data[0]?.id || "");
      const last = d.data.find((x) => x.id === localStorage.getItem(LAST_KEY)) || d.data[0];
      if (last) { setDesign(last); setCategory(last.category || "general"); }
    }).catch(() => toast.error("Could not load the Studio"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fitH = () => {
      if (rootRef.current) setHeight(Math.max(560, window.innerHeight - rootRef.current.getBoundingClientRect().top - 8));
    };
    fitH();
    window.addEventListener("resize", fitH);
    return () => window.removeEventListener("resize", fitH);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => setScale(Math.max(1, Math.min((el.clientWidth - 48) / PAGE.w, (el.clientHeight - 80) / PAGE.h)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [design?.id]);

  const persist = useCallback(async (d) => {
    setSaveState("saving");
    try {
      const body = { name: d.name, category: d.category || "general", background: d.background, elements: d.elements, based_on: d.based_on || null };
      const r = await api.put(`/graphic-studio/designs/${d.id}`, body);
      setSaved((list) => list.map((x) => (x.id === r.data.id ? r.data : x)));
      setSaveState("saved");
      if (designRef.current?.id === d.id) setDirty(false);
    } catch (ex) {
      setSaveState("error");
      toast.error(formatApiError(ex.response?.data?.detail) || "Save failed");
    }
  }, []);

  // Autosave: every edit is written to the server shortly after it happens.
  useEffect(() => {
    if (!dirty || !design?.id) return undefined;
    const t = setTimeout(() => persist(design), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [design, dirty, persist]);

  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    const flush = () => { if (dirtyRef.current && designRef.current?.id) persist(designRef.current); };
    window.addEventListener("beforeunload", flush);
    return () => { window.removeEventListener("beforeunload", flush); flush(); };
  }, [persist]);

  const edit = (fn) => { setDesign((d) => (d ? fn(d) : d)); setDirty(true); };
  const update = useCallback((id, patch) => {
    setDesign((d) => (d ? { ...d, elements: d.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)) } : d));
    setDirty(true);
  }, []);
  const addEl = (el) => {
    edit((d) => {
      const n = d.elements.length;
      const placed = el.type === "rect" ? el : { ...el, y: el.y + (n % 6) * 8 };
      return { ...d, elements: el.type === "rect" ? [placed, ...d.elements] : [...d.elements, placed] };
    });
    setSelectedId(el.id);
  };
  const removeEl = (id) => { edit((d) => ({ ...d, elements: d.elements.filter((e) => e.id !== id) })); setSelectedId(null); };
  const duplicateEl = (id) => {
    const src = design?.elements.find((e) => e.id === id);
    if (src) addEl({ ...src, id: newId(), x: src.x + 5, y: src.y + 5 });
  };
  const reorder = (id, dir) => edit((d) => {
    const i = d.elements.findIndex((e) => e.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= d.elements.length) return d;
    const els = [...d.elements];
    [els[i], els[j]] = [els[j], els[i]];
    return { ...d, elements: els };
  });

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId || !design || ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const step = e.shiftKey ? 5 : 1;
      const el = design.elements.find((x) => x.id === selectedId);
      if (!el) return;
      if (e.key === "ArrowLeft") update(selectedId, { x: el.x - step });
      else if (e.key === "ArrowRight") update(selectedId, { x: el.x + step });
      else if (e.key === "ArrowUp") update(selectedId, { y: el.y - step });
      else if (e.key === "ArrowDown") update(selectedId, { y: el.y + step });
      else if (e.key === "Delete" || e.key === "Backspace") removeEl(selectedId);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const loadDesign = (d) => {
    if (dirty && design?.id) persist(design);
    setDesign({ ...d, elements: d.elements.map((e) => ({ ...e })) });
    setCategory(d.category || "general");
    setSelectedId(null);
    setDirty(false);
    localStorage.setItem(LAST_KEY, d.id);
  };

  const createDesign = async (stock) => {
    setBusy(true);
    try {
      const body = stock
        ? stockDesignFor(stock, category)
        : { name: `${categoryLabel(category)} · Blank`, category, background: "#ffffff", elements: [], based_on: null };
      const r = await api.post("/graphic-studio/designs", body);
      setSaved((list) => [r.data, ...list]);
      loadDesign(r.data);
      toast.success(`Added “${r.data.name}”`);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Could not add certificate");
    } finally { setBusy(false); }
  };

  const applyLook = (stock) => {
    if (!window.confirm(`Replace the layout of “${design.name}” with the ${stock.name} look? Your text edits on this certificate will be lost.`)) return;
    const fresh = stockDesignFor(stock, design.category || "general");
    edit((d) => ({ ...d, based_on: stock.id, background: fresh.background, elements: fresh.elements }));
    setSelectedId(null);
  };

  const saveAs = async () => {
    const name = nameDraft.trim();
    if (!name || !design) return;
    setBusy(true);
    try {
      const r = await api.post("/graphic-studio/designs", { ...design, id: undefined, name, category: design.category || "general" });
      setSaved((list) => [r.data, ...list]);
      loadDesign(r.data);
      setNameOpen(false);
      toast.success(`Saved copy “${r.data.name}”`);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const onDelete = async (d) => {
    if (!window.confirm(`Delete certificate “${d.name}”?`)) return;
    await api.delete(`/graphic-studio/designs/${d.id}`);
    const rest = saved.filter((x) => x.id !== d.id);
    setSaved(rest);
    if (design?.id === d.id) {
      const next = rest.find((x) => (x.category || "general") === (d.category || "general")) || rest[0] || null;
      if (next) loadDesign(next); else { setDesign(null); setDirty(false); }
    }
    toast.success("Certificate deleted");
  };

  const previewRow = useMemo(() => {
    const r = rows[previewIdx];
    return r ? { ...r, club: r.club || clubName } : { ...SAMPLE_ROW, club: clubName };
  }, [rows, previewIdx, clubName]);

  const assets = useMemo(() => ({ logo: club?.logo_data_url || "", signature: club?.signature_data_url || "" }), [club]);
  const saveSignature = async (dataUrl) => {
    try {
      const r = await api.put("/graphic-studio/signature", { data_url: dataUrl });
      setClub((c) => ({ ...c, signature_data_url: r.data.signature_data_url }));
      toast.success("Signature saved — it now appears on every certificate");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Could not save signature");
      throw ex;
    }
  };
  const clearSignature = async () => {
    if (!window.confirm("Remove the saved signature? Signature slots on certificates will show as empty.")) return;
    await api.delete("/graphic-studio/signature");
    setClub((c) => ({ ...c, signature_data_url: "" }));
    toast.success("Signature removed");
  };

  const generate = async () => {
    if (!design) return;
    const chosen = rows.filter((r) => selected.has(r.id)).map((r) => ({ ...r, club: r.club || clubName }));
    const list = chosen.length ? chosen : [previewRow];
    setBusy(true);
    try {
      const doc = await buildPdf({ design, rows: list, layout, assets });
      doc.save(`certificates-${design.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${layout}.pdf`);
      toast.success(`${list.length} certificate${list.length === 1 ? "" : "s"} → PDF`);
    } catch (ex) {
      toast.error(ex.message || "PDF generation failed");
    } finally { setBusy(false); }
  };

  const selectedEl = design?.elements.find((e) => e.id === selectedId) || null;

  return (
    <div ref={rootRef} className="flex flex-col" style={{ height }} data-testid="graphic-studio-page">
      <div className="flex items-center gap-3 px-4 py-3 flex-wrap" style={{ borderBottom: "1px solid #1f1f1f" }}>
        <Link to="/awards" className="ccs-btn-ghost text-xs flex items-center gap-1" data-testid="studio-back"><ArrowLeft size={12} /> Season Stats</Link>
        <div className="min-w-0">
          <div className="ccs-label text-[10px]">Graphic Studio{design ? ` · ${categoryLabel(design.category)}` : ""}</div>
          <input value={design?.name || ""} disabled={!design} onChange={(e) => edit((d) => ({ ...d, name: e.target.value }))}
            className="bg-transparent text-lg font-semibold tracking-tight outline-none border-b border-transparent focus:border-amber-400 w-72" data-testid="design-name-input" />
          <span className="muted-text text-[10px] ml-2 inline-flex items-center gap-1" data-testid="save-status">
            {dirty || saveState === "saving" ? <><Loader2 size={10} className="animate-spin" /> Saving…</>
              : saveState === "error" ? <span className="text-red-400">Not saved</span>
              : <><Check size={10} /> Saved</>}
          </span>
        </div>
        <div className="flex-1" />
        <div className="inline-flex" style={{ border: "1px solid #1f1f1f" }} data-testid="layout-toggle">
          {[["a4", "A4"], ["a5x2", "2-up A5"]].map(([v, l]) => (
            <button key={v} onClick={() => setLayout(v)} className="px-3 py-1.5 text-xs" data-testid={`layout-${v}`}
              style={{ background: layout === v ? "#1d160a" : "transparent", color: layout === v ? "#fbbf24" : "#999" }}>{l}</button>
          ))}
        </div>
        <button onClick={generate} disabled={busy || !design} className="ccs-btn text-xs flex items-center gap-1" data-testid="generate-pdf">
          <FileDown size={12} /> PDF ({selected.size || 1})
        </button>
      </div>

      <div className="flex-1 grid min-h-0" style={{ gridTemplateColumns: "250px 1fr 460px" }}>
        <aside className="overflow-y-auto p-3 space-y-5" style={{ borderRight: "1px solid #1f1f1f" }}>
          <section className="space-y-2" data-testid="design-save-panel">
            <div className="ccs-label text-[10px]">This certificate</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button onClick={() => design && persist(design)} disabled={busy || !design} className="ccs-btn text-xs flex items-center justify-center gap-1" data-testid="design-save"><Save size={12} /> Save</button>
              <button onClick={() => { setNameDraft(`${design?.name || ""} copy`); setNameOpen(true); }} disabled={busy || !design} className="ccs-btn-ghost text-xs" data-testid="design-save-as">Save as…</button>
            </div>
            {nameOpen && (
              <form onSubmit={(e) => { e.preventDefault(); saveAs(); }} className="space-y-1.5" data-testid="design-name-popover">
                <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} className="ccs-input text-xs w-full" placeholder="Certificate name" data-testid="design-name-draft" />
                <div className="grid grid-cols-2 gap-1.5">
                  <button type="submit" className="ccs-btn text-xs" data-testid="design-name-confirm">Save copy</button>
                  <button type="button" onClick={() => setNameOpen(false)} className="ccs-btn-ghost text-xs">Cancel</button>
                </div>
              </form>
            )}
          </section>
          <section>
            <div className="ccs-label text-[10px] mb-2">Certificates</div>
            <DesignsPanel saved={saved} current={design} category={category} onCategory={setCategory}
              onLoad={loadDesign} onNew={createDesign} onApplyLook={applyLook} onDelete={onDelete} />
          </section>
          <section><div className="ccs-label text-[10px] mb-2">Insert</div><InsertToolbar onAdd={addEl} background={design?.background} onBackground={(c) => edit((d) => ({ ...d, background: c }))} hasSignature={!!assets.signature} onSaveSignature={saveSignature} /></section>
          <section><div className="ccs-label text-[10px] mb-2">Signature library</div><SignatureLibrary signature={assets.signature} onSave={saveSignature} onClear={clearSignature} /></section>
          <section><div className="ccs-label text-[10px] mb-2">Selected element</div><ElementInspector el={selectedEl} onUpdate={update} onDelete={removeEl} onDuplicate={duplicateEl} onReorder={reorder} /></section>
        </aside>

        <main ref={wrapRef} className="relative overflow-auto flex items-start justify-center p-6" style={{ background: "#0a0a0a" }}>
          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 text-[11px] muted-text" data-testid="preview-nav">
            <Eye size={11} />
            {rows.length > 0 ? (
              <>
                <button onClick={() => setPreviewIdx((i) => Math.max(0, i - 1))} className="hover:text-amber-400" data-testid="preview-prev"><ChevronLeft size={12} /></button>
                <span data-testid="preview-label">{previewIdx + 1} / {rows.length} · {previewRow.name || "—"}</span>
                <button onClick={() => setPreviewIdx((i) => Math.min(rows.length - 1, i + 1))} className="hover:text-amber-400" data-testid="preview-next"><ChevronRight size={12} /></button>
              </>
            ) : <span data-testid="preview-label">Sample data — load a source on the right</span>}
          </div>
          <div className="mt-5">
            {design ? (
              <StudioCanvas design={design} row={previewRow} assets={assets} scale={scale}
                selectedId={selectedId} onSelect={setSelectedId} onUpdate={update} />
            ) : (
              <div className="muted-text text-sm mt-20" data-testid="no-design">Add a certificate from the left to start.</div>
            )}
          </div>
        </main>

        <aside className="overflow-hidden p-3" style={{ borderLeft: "1px solid #1f1f1f" }}>
          <DataPanel seasons={seasons} seasonId={seasonId} onSeasonId={setSeasonId} source={source}
            onSource={(s) => { setSource(s); setCategory(s); }} rows={rows} onRows={setRows}
            selected={selected} onSelected={setSelected} previewIdx={previewIdx} onPreviewIdx={setPreviewIdx} clubName={clubName} />
        </aside>
      </div>
    </div>
  );
}
